import React, { useEffect, useState } from "react";
import { Alert, Button, Descriptions, Form, Input, message, Modal, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import {
  combineInvoices,
  generateCombinedInvoiceReference,
  getInvoicesWithEdiOrders,
} from "../../../../../functions/invoices";
import { formatPrice } from "../../../../../functions/formatters";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import dayjs from "dayjs";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const CombineInvoicesModal = ({ visible, onClose, onSuccess, selectedInvoices, profile }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [referenceText, setReferenceText] = useState("");
  // null = nog aan het controleren
  const [ediInvoiceIds, setEdiInvoiceIds] = useState(null);

  useEffect(() => {
    if (!visible || selectedInvoices.length === 0) return;
    let cancelled = false;
    setEdiInvoiceIds(null);
    getInvoicesWithEdiOrders(selectedInvoices)
      .then((invoices) => !cancelled && setEdiInvoiceIds(invoices.map((inv) => inv.id)))
      .catch((error) => {
        console.error("Error checking EDI orders:", error);
        // combineInvoices controleert zelf ook nog; niet blokkeren op een mislukte check.
        if (!cancelled) setEdiInvoiceIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, selectedInvoices]);

  // Generate default reference when modal opens
  useEffect(() => {
    if (visible && selectedInvoices.length > 0) {
      const defaultRef = generateCombinedInvoiceReference(selectedInvoices.length);
      setReferenceText(defaultRef);
      form.setFieldsValue({ reference: defaultRef });
    }
  }, [visible, selectedInvoices, form]);

  // Reset when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setReferenceText("");
    }
  }, [visible, form]);

  // Calculate totals
  const totals = {
    orders: selectedInvoices.reduce((sum, inv) => sum + (inv.farmorderIds?.length || 0), 0),
    products: selectedInvoices.reduce((sum, inv) => sum + (inv.userShoppingCart?.length || 0), 0),
    subTotal: selectedInvoices.reduce((sum, inv) => sum + (inv.subTotalAmount || 0), 0),
    shipping: selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.shippingCost || 0), 0),
    total: selectedInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      // Get invoice IDs
      const invoiceIds = selectedInvoices.map((inv) => inv.id);

      // Combine invoices
      const combinedInvoiceId = await combineInvoices(invoiceIds, referenceText, profile);

      // Close modal and refresh
      handleClose();
      if (onSuccess) {
        onSuccess(combinedInvoiceId);
      }
    } catch (error) {
      console.error("Error combining invoices:", error);
      message.error(t("notifications.error_combining_invoices") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setReferenceText("");
    onClose();
  };

  // Validation: check if all invoices are from same customer
  const allSameCustomer = () => {
    if (selectedInvoices.length === 0) return true;
    const customerIds = new Set(selectedInvoices.map((inv) => inv.farmorderCustomerId));
    return customerIds.size === 1;
  };

  // Validation: check if all invoices are draft
  const allDraft = () => {
    return selectedInvoices.every((inv) => inv.status === "draft");
  };

  const hasEdiInvoices = ediInvoiceIds?.length > 0;
  const canCombine =
    allSameCustomer() && allDraft() && selectedInvoices.length >= 2 && ediInvoiceIds !== null && !hasEdiInvoices;

  // Table columns for preview
  const columns = [
    {
      title: t("invoice_id"),
      dataIndex: "id",
      render: (id) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {id} {ediInvoiceIds?.includes(id) && <Tag color="red">EDI</Tag>}
        </span>
      ),
    },
    {
      title: t("delivery_date"),
      dataIndex: "farmorderDeliveryDate",
      render: (date) => dayjs(date).format("D MMM YYYY"),
    },
    {
      title: t("products"),
      dataIndex: "userShoppingCart",
      render: (products) => `${products?.length || 0} items`,
    },
    {
      title: t("subtotal"),
      dataIndex: "subTotalAmount",
      render: (amount) => CURRENCY + formatPrice(amount),
    },
    {
      title: t("total"),
      dataIndex: "totalAmount",
      render: (amount) => CURRENCY + formatPrice(amount),
    },
  ];

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
          {t("combine_invoices")}
        </span>
      }
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading || (visible && ediInvoiceIds === null)}
          onClick={handleSubmit}
          disabled={!canCombine}
        >
          {t("combine_invoices_confirm")}
        </Button>,
      ]}
    >
      {/* Validation warnings */}
      {!allSameCustomer() && (
        <Alert
          type="error"
          message={t("combine_invoices_error_different_customers")}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      {!allDraft() && (
        <Alert type="error" message={t("combine_invoices_error_not_all_draft")} style={{ marginBottom: 16 }} showIcon />
      )}
      {hasEdiInvoices && (
        <Alert
          type="error"
          message={t("combine_invoices_error_edi", { count: ediInvoiceIds.length })}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      {selectedInvoices.length < 2 && (
        <Alert type="warning" message={t("combine_invoices_error_minimum_two")} style={{ marginBottom: 16 }} showIcon />
      )}

      {/* Info alert */}
      {canCombine && (
        <Alert
          type="info"
          message={t("combine_invoices_info_title")}
          description={
            <div>
              <p style={{ marginBottom: 8 }}>
                {t("combine_invoices_info_description", {
                  count: selectedInvoices.length,
                  customer: selectedInvoices[0]?.farmorderCustomerName,
                })}
              </p>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>{t("combine_invoices_info_point_1")}</li>
                <li>{t("combine_invoices_info_point_2")}</li>
                <li>{t("combine_invoices_info_point_3")}</li>
              </ul>
            </div>
          }
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Summary statistics */}
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t("invoices_to_combine")}>
          <Tag color="blue">{selectedInvoices.length}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("customer_name")}>
          {selectedInvoices[0]?.farmorderCustomerName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("total_orders")}>{totals.orders}</Descriptions.Item>
        <Descriptions.Item label={t("total_products")}>{totals.products}</Descriptions.Item>
        <Descriptions.Item label={t("subtotal")}>
          {CURRENCY}
          {formatPrice(totals.subTotal)}
        </Descriptions.Item>
        <Descriptions.Item label={t("shipping_costs")}>
          {CURRENCY}
          {formatPrice(totals.shipping)}
        </Descriptions.Item>
        <Descriptions.Item label={t("total_amount")} span={2}>
          <strong>
            {CURRENCY}
            {formatPrice(totals.total)}
          </strong>
        </Descriptions.Item>
      </Descriptions>

      {/* Reference field */}
      <Form form={form} layout="vertical">
        <Form.Item
          name="reference"
          label={t("reference")}
          help={`${referenceText.length}/30 ${t("characters")}`}
          rules={[
            { required: true, message: t("reference_required") },
            { max: 30, message: t("reference_max_30_chars") },
          ]}
        >
          <Input
            value={referenceText}
            onChange={(e) => {
              const value = e.target.value.substring(0, 30);
              setReferenceText(value);
              form.setFieldsValue({ reference: value });
            }}
            placeholder="W3 Jan 2025 | 3 orders"
            maxLength={30}
          />
        </Form.Item>
      </Form>

      {/* Invoice preview table */}
      <div style={{ marginTop: 16 }}>
        <h4>{t("invoices_to_be_combined")}:</h4>
        <Table
          columns={columns}
          dataSource={selectedInvoices}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      </div>
    </Modal>
  );
};

export default CombineInvoicesModal;
